import { h, Fragment } from 'preact'
import { useSignalEffect } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import { PAGE_SIZE } from '@tshmanager/chat-shared'
import { RoomList } from './RoomList.jsx'
import { RoomHeader } from './RoomHeader.jsx'
import { MessageList } from './MessageList.jsx'
import { Composer } from './Composer.jsx'
import { TypingIndicator } from './TypingIndicator.jsx'
import { FloatPanel } from './FloatPanel.jsx'
import { ContactProfilePanel } from './ContactProfilePanel.jsx'
import { CableStatusLine } from './CableStatusLine.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'

/**
 * ChatApp — top-level Preact root for the team chat UI.
 *
 * Props:
 *   store    — createChatStore() instance (signals)
 *   cable    — connectChatCable() return value { subscribeRoom, unsubscribeRoom, disconnect }
 *   mode     — "fullscreen" | "float"
 *   logoUrl  — brand mark next to sidebar CHAT label (chat.* product host only)
 *   showBack — back-to-app control before CHAT label (app.* fullscreen, not product host)
 *
 * Fullscreen: no chrome topbar. Sidebar RoomList + main panel.
 * Float: only the main panel — no sidebar.
 */
export function ChatApp({ store, cable, mode = 'fullscreen', logoUrl = '', showBack = false }) {
  const prevRoomIdRef = useRef(null)

  // When activeRoomId changes: subscribe to new room cable, unsubscribe from
  // previous room, load initial messages and mark room as read.
  useSignalEffect(() => {
    const roomId = store.activeRoomId.value
    const prevId = prevRoomIdRef.current

    if (roomId === prevId) return

    if (prevId != null) {
      cable.unsubscribeRoom(prevId)
    }

    if (roomId != null) {
      cable.subscribeRoom(roomId)

      // Load first page of messages if not already loaded
      const existing = store.messagesByRoom.value[roomId]
      if (!existing || existing.length === 0) {
        store.loadingRoomMsgs.value = roomId
        chatApi.messages.list(roomId).then(async res => {
          if (res.ok) {
            const data = await res.json
            const msgs = data.messages ?? data ?? []
            const hasMore = data.has_more ?? msgs.length >= PAGE_SIZE
            const oldest = msgs[0]?.id ?? null
            store.messagesByRoom.value = {
              ...store.messagesByRoom.value,
              [roomId]: msgs,
            }
            store.hasMoreByRoom.value = {
              ...store.hasMoreByRoom.value,
              [roomId]: hasMore,
            }
            if (oldest) {
              store.oldestIdByRoom.value = {
                ...store.oldestIdByRoom.value,
                [roomId]: oldest,
              }
            }
          }
        }).catch(() => { /* ignore */ })
          .finally(() => {
            if (store.loadingRoomMsgs.value === roomId) {
              store.loadingRoomMsgs.value = null
            }
          })
      }

      // Mark room as read
      chatApi.messages.read(roomId).catch(() => { /* non-fatal */ })
    }

    prevRoomIdRef.current = roomId
  })

  // Load room list on mount
  useEffect(() => {
    store.roomsLoading.value = true
    store.roomsLoadError.value = null
    chatApi.rooms.list().then(async res => {
      if (res.ok) {
        const data = await res.json
        store.rooms.value = Array.isArray(data) ? data : (data.rooms ?? [])
        store.roomsLoadError.value = null
      } else {
        store.roomsLoadError.value = t('chat.rooms_load_error')
      }
    }).catch(() => {
      store.roomsLoadError.value = t('chat.rooms_load_error')
    }).finally(() => { store.roomsLoading.value = false })
  }, []) // eslint-disable-line

  const activeRoomId = store.activeRoomId.value
  const floatPanels  = store.floatPanels.value

  if (mode === 'float') {
    return (
      <div class="chat-float-inner">
        <RoomHeader store={store} cable={cable} />
        <MessageList store={store} cable={cable} />
        <TypingIndicator store={store} />
        {activeRoomId && (
          <Composer
            store={store}
            cable={cable}
            roomId={activeRoomId}
            personId={store.personId}
          />
        )}
      </div>
    )
  }

  return (
    <div class="chat-app chat-app--fullscreen">
      <div class="chat-fullscreen-body chat-fullscreen-body--preact">
        <aside class="chat-sidebar">
          <RoomList store={store} cable={cable} logoUrl={logoUrl} showBack={showBack} />
        </aside>

        <div class="chat-main">
          <CableStatusLine store={store} />
          {activeRoomId ? (
            <>
              <RoomHeader store={store} cable={cable} />
              <MessageList store={store} cable={cable} />
              <TypingIndicator store={store} />
              <Composer
                store={store}
                cable={cable}
                roomId={activeRoomId}
                personId={store.personId}
              />
            </>
          ) : (
            <div class="chat-main-empty">
              <i class="bi bi-chat-dots chat-main-empty-icon" />
              <p class="chat-main-empty-text">{t('chat.select_room')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Float panels rendered by the dock — one <FloatPanel> per entry */}
      {floatPanels.map((panel, idx) => (
        <FloatPanel
          key={panel.roomId}
          dockStore={store}
          panelRoomId={panel.roomId}
          personId={store.personId}
          personName={store.personName}
          tenantId={store.tenantId}
          panelIndex={idx}
          minimized={panel.minimized}
        />
      ))}

      {/* Contact profile panel — opens when clicking an incoming message avatar */}
      <ContactProfilePanel store={store} />
    </div>
  )
}
